import { Button, DeprecatedExternalLinkIcon as ExternalLink, Flex, Link } from '@lokalise/louis';
import { Formik } from 'formik';
import { useState } from 'react';

import type { AddContributorsPayload, Project } from '@app/api/team';
import { useAddContributorsMutation } from '@app/api/team';
import { scheduleToast, showUnexpectedErrorToast } from '@app/components/Toast';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import { ModalFooter } from '../styles';
import type { AddExistingUsersFormSubmitValues } from '../types';
import { Role } from '../types';
import { AddExistingUsersFormValidationSchema } from '../utils';
import AddExistingUsersForm from './AddExistingUsersForm';

type AddExistingUsersProps = {
    project: Project;
    teamMembers: { value: string; label: string }[];
    onClose: () => void;
};

const initialValues = {
    teamMembers: [],
    role: {
        label: 'Translator',
        value: Role.translator,
    },
    isReviewer: false,
    referenceLanguages: null,
    contributableLanguages: null,
};

const AddExistingUsers = ({ project, teamMembers, onClose }: AddExistingUsersProps) => {
    const [usersCount, setUsersCount] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addTeamContributors] = useAddContributorsMutation();

    const onValidateHandler = (isReady: boolean) => {
        if (!isReady) {
            setIsSubmitting(false);
        }
    };

    const onSubmitHandler = async (formValues: AddExistingUsersFormSubmitValues) => {
        const {
            teamMembers: formTeamMembers,
            role,
            isReviewer,
            referenceLanguages,
            contributableLanguages,
        } = formValues;

        const payload: AddContributorsPayload = {
            projectId: project.id,
            emails: formTeamMembers.map(({ value }) => value),
            isAdmin: role.value === Role.admin,
            isReviewer,
            referenceLanguages: referenceLanguages?.map((lang) => lang.value),
            contributableLanguages: contributableLanguages?.map((lang) => lang.value),
        };

        const response = await addTeamContributors(payload);
        if ('error' in response) {
            showUnexpectedErrorToast();
            setIsSubmitting(false);
        } else {
            track(AnalyticsEventName.ADD_EXISTING_CONTRIBUTOR, {
                /* eslint-disable @typescript-eslint/naming-convention */
                users_quantity: formTeamMembers.length,
                project_name: project.name,
                project_id: project.id,
                role: role.label,
                reviewer: isReviewer,
                reference_languages: referenceLanguages?.map(({ label }) => label).join(),
                reference_languages_quantity: referenceLanguages?.length,
                contributable_languages: contributableLanguages?.map(({ label }) => label).join(),
                contributable_languages_quantity: contributableLanguages?.length,
                /* eslint-enable @typescript-eslint/naming-convention */
            });
            scheduleToast({
                type: 'success',
                title: `Contributor(-s) added successfully`,
            });
            window.location.reload();
        }
    };

    const getButtonLabel = () => {
        switch (true) {
            case usersCount === 0:
                return 'Add user';

            case usersCount === 1:
                return `Add ${usersCount} user`;

            case usersCount > 1:
                return `Add ${usersCount} users`;

            default:
                return null;
        }
    };

    return (
        <>
            <Formik<AddExistingUsersFormSubmitValues>
                initialValues={initialValues}
                validationSchema={AddExistingUsersFormValidationSchema}
                onSubmit={onSubmitHandler}
                validateOnChange
            >
                <AddExistingUsersForm
                    project={project}
                    teamMembers={teamMembers}
                    isSubmitting={isSubmitting}
                    onValidate={onValidateHandler}
                    onFormChange={(formValues) => setUsersCount(formValues.teamMembers.length)}
                />
            </Formik>
            <ModalFooter justify="space-between" align="center">
                <Link
                    href="https://docs.lokalise.com/translation-workflow/contributors"
                    target="_blank"
                    rel="noreferrer"
                >
                    <Flex align="center" gap={1}>
                        Learn more about contributors. <ExternalLink />
                    </Flex>
                </Link>
                <Flex justify="flex-end" gap={2} grow={1}>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => setIsSubmitting(true)} loading={isSubmitting}>
                        {getButtonLabel()}
                    </Button>
                </Flex>
            </ModalFooter>
        </>
    );
};

export default AddExistingUsers;
